<template>
  <div class="editor">
    <p
      v-if="label"
      :class="['editor__title v-label mb-1 mt-0', {'error--text': errorMessages.length > 0}, {'pb-2': !inlineRow}]"
    >
      {{ label }}
    </p>
    <div :class="[{'w-100 d-flex flex-row-reverse': inlineRow}]">
      <input-dialog
        v-if="linkInputDialog"
        v-model="linkUrl"
        label-title="Link"
        rules="required|url"
        title="Add Link"
        :visible="linkInputDialog"
        @close="linkInputDialog = false"
        @confirm="editorToggleLink"
      />
      <div class="menubar d-flex flex-row flex-wrap flex-grow-0 flex-shrink-0 row-gap-2">
        <slot
          name="menubar"
          :editor="editor"
        />
        <div
          v-if="richtextAllowed || boldAllowed || italicAllowed || hrAllowed || linkAllowed || alignAllowed"
          class="d-flex flex-row flex-wrap row-gap-2"
        >
          <filled-button
            v-if="richtextAllowed || boldAllowed"
            class="mr-2"
            :filled="isBoldMark"
            icon="mdi-format-bold"
            :tooltip="$t('button.bold')"
            @click="editor?.chain().focus().toggleBold().run()"
          />
          <filled-button
            v-if="richtextAllowed || italicAllowed"
            class="mr-2"
            :filled="isItalicMark"
            icon="mdi-format-italic"
            :tooltip="$t('button.italic')"
            @click="editor?.chain().focus().toggleItalic().run()"
          />

          <div
            v-if="richtextAllowed || alignAllowed"
            class="align-btn-group"
          >
            <filled-button
              :filled="isLeftMark"
              icon="mdi-format-align-left"
              :tooltip="$t('button.align_text_left')"
              @click="editor?.chain().focus().setTextAlign('left').run()"
            />
            <filled-button
              :filled="isCenterMark"
              icon="mdi-format-align-center"
              :tooltip="$t('button.align_text_center')"
              @click="editor?.chain().focus().setTextAlign('center').run()"
            />
            <filled-button
              class="mr-2"
              :filled="isRightMark"
              icon="mdi-format-align-right"
              :tooltip="$t('button.align_text_right')"
              @click="editor?.chain().focus().setTextAlign('right').run()"
            />
          </div>

          <filled-button
            v-if="richtextAllowed || hrAllowed"
            class="mr-2"
            icon="mdi-minus"
            :tooltip="$t('button.horizontal_rule')"
            @click="editor?.chain().focus().setHorizontalRule().run()"
          />
          <filled-button
            v-if="richtextAllowed || linkAllowed"
            class="mr-2"
            :disabled="isHyperlink || !isHyperlink && editor?.view.state.selection.empty"
            icon="mdi-link-variant"
            :tooltip="$t('button.add_hyperlink')"
            @click="linkInputDialog = true"
          />
          <filled-button
            v-if="richtextAllowed || linkAllowed"
            class="mr-2"
            :disabled="!isHyperlink"
            icon="mdi-link-variant-off"
            :tooltip="$t('button.remove_hyperlink')"
            @click="editor?.chain().focus().unsetLink().run()"
          />
        </div>
      </div>

      <editor-content
        :class="[
          {'editor__content-dark': dark},
          {'editor__content-error': errorMessages.length > 0},
          {'mt-2': dense && !inlineRow},
          {'mt-5': !dense && !inlineRow},
          {'mt-0 w-100 mr-4': inlineRow},
          'editor__content text--primary']"
        :editor="editor"
      />
    </div>
    <div
      v-if="hint || errorMessages.length > 0"
      class="d-flex w-100 flex-row"
    >
      <p class="field-error-message mt-1 mb-0">
        {{ errorMessages.join(' ') }}&nbsp;
      </p>
      <p>
        {{ hint }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import Vue, {
  PropType, computed, defineComponent, onBeforeUnmount, ref,
  watch,
} from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-2';
import { Handlebar } from '@/services/handlebar-tiptap-extension';
import { textWithHandlebarsToHtml, htmlToTextWithHandlebars } from '@/services/handlebar-utils';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import { TranslateResult } from 'vue-i18n';
import { FixedBlock } from '@/services/tiptap-fixedblock';
import { useHandlebars } from '@/composables/messagecenter';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import { CommunicationFlags } from '@/grpc-api/model/message-settings';
import { useVuetify } from '../helpers/vue2-helpers';

const props = defineProps({
  value: { type: String, required: false, default: '' },
  dense: { type: Boolean, required: false, default: false },
  richtextAllowed: { type: Boolean, required: false, default: false },
  boldAllowed: { type: Boolean, required: false, default: false },
  italicAllowed: { type: Boolean, required: false, default: false },
  hrAllowed: { type: Boolean, required: false, default: false },
  linkAllowed: { type: Boolean, required: false, default: false },
  alignAllowed: { type: Boolean, required: false, default: false },

  encodeLinebreak: { type: Boolean, required: false, default: false },
  errorMessages: { type: Array<String>, required: false, default: [] },
  hint: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
  height: { type: String, required: false, default: '200px' },
  label: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: undefined },
  inlineRow: { type: Boolean, required: false, default: false },

  disabled: { type: Boolean, required: false, default: false },
});

const emit = defineEmits<{(e:'input', event: string | null): void}>();

const vuetify = useVuetify();

const editor = ref(undefined as undefined | Editor);

const handlebars = useHandlebars;

const linkInputDialog = ref(false);
const linkUrl = ref(undefined as undefined | string);

const editorToggleLink = (href: string) => {
  editor.value?.commands.toggleLink({ href, target: '_blank' });
};

const dark = computed(() => vuetify.theme.dark);

const isBoldMark = computed(() => editor.value!.isActive('bold') || false);
const isItalicMark = computed(() => editor.value!.isActive('italic') || false);
const isHyperlink = computed(() => editor.value!.isActive('link') || false);

const isLeftMark = computed(() => editor.value?.isActive({ textAlign: 'left' }) || false);
const isCenterMark = computed(() => editor.value?.isActive({ textAlign: 'center' }) || false);
const isRightMark = computed(() => editor.value?.isActive({ textAlign: 'right' }) || false);

const handlebarsById = computed(() => store.getters.messageCenterHandlebarsById);

watch(() => props.value, () => {
  if (editor.value!.getHTML() === textWithHandlebarsToHtml(props.value, handlebars.value)) { return; }
  editor.value!.commands.setContent(textWithHandlebarsToHtml(props.value, handlebars.value));
});

// @ts-ignore
editor.value = new Editor({
  editable: !props.disabled,
  extensions: [
    StarterKit.configure({
      bulletList: false,
      blockquote: false,
      listItem: false,
      orderedList: false,
      code: false,
      codeBlock: false,
      heading: false,
    }),
    Link,
    Handlebar,
    FixedBlock,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
  ],
  content: textWithHandlebarsToHtml(props.value, handlebars.value),
  onUpdate: ({ editor: e }) => {
    const content = e.getHTML();
    let newContent = htmlToTextWithHandlebars(content, handlebarsById.value);
    if (props.encodeLinebreak && newContent) {
      newContent = newContent.replaceAll(/\n/g, '<br />');
    }
    emit('input', newContent);
  },
});

onBeforeUnmount(() => {
  editor.value!.destroy();
  editor.value = undefined;
});

// Driven by settings → "Show date, time, and party size in reservation details block"
const showDetailsInSummary = computed(() => !store.getters.messageCenterSettings
  ?.communicationFlags
  ?.includes(CommunicationFlags.HideSummaryDetails));

const svgBackground = computed(() => {
  const text = i18n.t('label.reservation_details');
  const partySizeLabel = i18n.t('label.party_size');
  const dateLabel = i18n.t('label.date');
  const timeLabel = i18n.t('label.time');
  const fgColor = vuetify.theme.dark ? '#D6D6D8' : '#53535D';
  const showDetails = showDetailsInSummary.value;

  // Party size / date / time column — only when the settings switch is on
  const detailsColumns = showDetails ? `
  <text x='89' y='56' text-anchor='middle' fill='${fgColor}' font-size='10'\
  font-family='sans-serif'>${partySizeLabel}</text>
  <text x='199' y='56' text-anchor='middle' fill='${fgColor}' font-size='10'\
  font-family='sans-serif'>${dateLabel}</text>
  <text x='309' y='56' text-anchor='middle' fill='${fgColor}' font-size='10'\
  font-family='sans-serif'>${timeLabel}</text>
  <rect x='50'  y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>
  <rect x='160' y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>
  <rect x='270' y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>` : '';

  const buttonY = showDetails ? 86 : 46;
  const bottomLineY = showDetails ? 116 : 86;
  const height = showDetails ? 130 : 100;

  const svg = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 128'>
  <rect width='400' height='${height}' fill='transparent'/>
  <rect x='20' y='12' width='360' height='1' fill='#d7d7d7' rx='1'/>
  <text x='200' y='32' text-anchor='middle' fill='${fgColor}' font-size='12'\
  font-family='sans-serif' font-weight='medium'>${text}</text>
  ${detailsColumns}
  <rect x='140' y='${buttonY}' width='120' height='20' rx='4' fill='#C6C6C8'/>
  <rect x='20' y='${bottomLineY}' width='360' height='1' fill='#d7d7d7' rx='1'/>
</svg>`;

  const cleanSvg = svg.replace(/\n/g, '').trim();

  return `url("data:image/svg+xml;utf8,${encodeURIComponent(cleanSvg)}")`;
});

const svgBackgroundHeight = computed(() => (showDetailsInSummary.value ? '130px' : '100px'));

// Placeholder for the SMS CTA block
const ctaSvg = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 32'>
  <rect width='400' height='32' fill='transparent'/>
  <g transform='translate(130, 9) scale(0.583)'>
    <path fill='#C6C6C8' d='M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0\
 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29\
 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z'/>
  </g>
  <rect x='150' y='12' width='120' height='8' rx='4' fill='#C6C6C8'/>
  <rect x='150' y='21' width='120' height='1' rx='0.5' fill='#C6C6C8'/>
</svg>`;
const cleanCtaSvg = ctaSvg.replace(/\n/g, '').trim();
const ctaSvgBackground = `url("data:image/svg+xml;utf8,${encodeURIComponent(cleanCtaSvg)}")`;

</script>

<style lang="scss">

.editor__title {
  font-size: 12px;
}

.editor__content {
  color: $black-color !important;
  border-bottom: 1px solid;
  outline: none;
  padding: 8px;
  min-height: v-bind(height);
  max-height: 100%;
  overflow-y: scroll;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.50) !important;

  &.editor__content-error {
    border: 1px solid $red-color !important;
  }
}

.editor__content-dark {
  border: 1px solid white !important;
}

.ProseMirror {
  min-height: v-bind(height);
  max-height: 100%;
}

.editor__content p {
  margin-bottom: 0px !important;
}

.editor__content {
  -webkit-touch-callout: auto;
  -webkit-user-select: auto;
  -khtml-user-select: auto;
  -moz-user-select: auto;
  -ms-user-select: auto;
  user-select: auto;
}

.ProseMirror {
  outline: none;
}

.handlebar {
  background: $primary-color;
  color: $white-color;
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 3px;
  margin-top: 1px;
  margin-bottom: 1px;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  white-space: nowrap;
  height: 24px;
  display: inline-block;
}

.tiptap-fixed-block, span[data-type="fixed-block"] {
  .theme--dark &{
    background-color: #3F3F49;
  }
  color: transparent;
  border-radius: 3px;
  margin-top: 1px;
  margin-bottom: 1px;
  display: inline-block;
  width: 100%;
  cursor: not-allowed;
  user-select: none;
  overflow: hidden;
  height: v-bind(svgBackgroundHeight);
  background-color: #F9F9F9;

  background-image: v-bind(svgBackground);
  background-repeat: no-repeat;
  background-position: 50% 0%;
  background-size: auto 128px;
  border-radius: 8px;
}

.tiptap-fixed-block[data-value="cta"], span[data-type="fixed-block"][data-value="cta"] {
  height: 32px;
  background-image: v-bind(ctaSvgBackground);
  background-size: auto 32px;
}

.align-btn-group {

  > div:nth-child(1) > button.v-btn {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    border-right: none;
  }

  > div:nth-child(2) > button.v-btn  {
    border-radius: 0px;
    border-right: none;
  }

  > div:nth-child(3) > button.v-btn  {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }
}

</style>
